import { createContext, type ReactNode, useContext } from "react";
import type { VisitExamVM } from "@/features/visits/components/visit-exam-card";

/**
 * Decouples *what a clinician did* from *where the write goes*.
 *
 * The command sink keeps charting controls independent from the mutation
 * transport. The tree issues clinical commands without knowing which API
 * procedures implement them.
 *
 * `VisitExamVM` is imported for its type only, so the cycle it would otherwise
 * form with the exam card is erased at compile time.
 */

/** The command surface a charting subtree may issue. Exam-shaped today. */
export type ExamCommands = Pick<
  VisitExamVM,
  | "onRecordFinding"
  | "onAnswerFindingComponent"
  | "onMarkFindingDNP"
  | "onMarkFindingUnableToPerform"
  | "onSetFindingComment"
  | "onClearFinding"
  | "onMarkFindingForTracking"
  | "onUnmarkFindingForTracking"
>;

const ChartCommandContext = createContext<ExamCommands | null>(null);

export function CommandSink({ commands, children }: { commands: ExamCommands; children: ReactNode }): ReactNode {
  return <ChartCommandContext.Provider value={commands}>{children}</ChartCommandContext.Provider>;
}

/** The active charting command surface. */
export function useChartCommands(): ExamCommands {
  const commands = useContext(ChartCommandContext);
  if (!commands) throw new Error("useChartCommands must be used inside a <CommandSink>");
  return commands;
}
